import gql from 'graphql-tag';
import {useQuery} from '@apollo/client';
import React, {useState} from 'react';
import ConfigurationFormField from '../components/ConfigurationFormField';
import RadioGroup from '../../../ui/RadioGroup';
import UIMultiSelectListBox from '../../../ui/form/multiSelectList/UIMultiSelectListBox';
import LanguageSelector from '../components/LanguageSelector';
import {ConfigurationRules, FreePaidStatus, PrivatePublicStatus} from '../../../models/form/ConfigurationRules';
import {IOption} from '../../../ui/form/UIOption';
import {LanguageId} from '../../../models/utils/Languages';
import {Category, CategoryVersion} from '../../../models/category/Category';
import UILink from '../../../ui/UILink';
import OldUrls from '../../../models/utils/OldUrls';
import ConfigurationRulesSkeleton from '../skeleton/ConfigurationRulesSkeleton';
import Tip from '../../../ui/Tip';
import {EditingRights} from '../../../models/form/EditingRights';
import HelpDialog from '../../../ui/modal/HelpDialog';

type ConfigurationRulesProps = {
  rules: ConfigurationRules
  editingRights: EditingRights
  updateRules: (rules: ConfigurationRules) => void
  excludeV1: boolean
}

export default function ConfigurationRulesBlock({
  rules,
  updateRules,
  editingRights,
  excludeV1
}: ConfigurationRulesProps) {

  const [showHelp, setShowHelp] = useState(false);
  const {loading, data, error} = useQuery<ConfigurationRulesBlockQueryData>(query, {
    errorPolicy: 'all',
  });

  if (error || loading) {
    return <ConfigurationRulesSkeleton/>;
  }

  const filterByVersion = (category: Category) => {
    if (excludeV1)
      return category.version !== CategoryVersion.V1_0;
    else
      return true;
  }

  const options = (data?.categories || []).filter(filterByVersion).map((x) => {
    const categoryTypes: IOption<boolean>[] = x.types.map((y) => ({
      id: y.id.toString(),
      label: y.name,
      value: y.name,
      selected: rules.eventTypeIds.indexOf(y.id) >= 0,
      parent: false,
    }));
    return {
      id: x.id,
      label: x.name,
      options: categoryTypes,
    };
  });

  const v1categories = (data?.categories || []).filter((x) => x.version === CategoryVersion.V1_0).map((x) => x.name);

  const onFreePaidStatusUpdate = (status: FreePaidStatus) => {
    updateRules({...rules, freePaidStatus: status});
  };

  const onPrivatePublicStatusUpdate = (status: PrivatePublicStatus) => {
    updateRules({...rules, privatePublicStatus: status});
  };

  const updateDefaultLanguage = (newDefaultLanguage: LanguageId) => {
    updateRules({...rules, defaultLanguage: newDefaultLanguage});
  };

  const onUpdateTypes = (typeIds: string[]) => {
    updateRules({...rules, eventTypeIds: typeIds});
  };

  return (
    <>
      <HelpDialog show={showHelp} onClose={() => setShowHelp(false)}>
        Management 3.0 uses on-site evaluation forms with a predefined set of questions. You cannot change neither
        the order of these fields, nor their configuration.
      </HelpDialog>

      <ConfigurationFormField label="Paid/Free" id="freePaidStatus"
        tooltip="Set Free (Paid) if you want the form template apply only to free (paid) events">
        <RadioGroup options={[{id: 'FreePaid', value: 'All'}, {id: 'OnlyFree', value: 'Free'}, {
          id: 'OnlyPaid',
          value: 'Paid',
        }]} selectedValue={rules.freePaidStatus} onChange={onFreePaidStatusUpdate}/>
      </ConfigurationFormField>
      <ConfigurationFormField label="Private/Public" id="privatePublicStatus"
        tooltip="Set Private (Public) if you want the form template apply only to private (public) events">
        <RadioGroup options={[{id: 'PrivatePublic', value: 'All'}, {id: 'OnlyPrivate', value: 'Private'}, {
          id: 'OnlyPublic',
          value: 'Public',
        }]} selectedValue={rules.privatePublicStatus} onChange={onPrivatePublicStatusUpdate}/>
      </ConfigurationFormField>
      <ConfigurationFormField id="eventTypeIds" label="Event types"
        tooltip={<div className="w-72">By selecting one or more event types, you order Workshop Butler to apply
                                       this form template only to the events of selected types. <br/>
                                       Leave it empty if you want the template to work with any event type.</div>}>
        <div className="flex flex-col w-full">
          {options.length > 0 &&
          <UIMultiSelectListBox options={options} onChange={onUpdateTypes}
            containerClassName="w-full" emptyLabel="No type selected"/>
          }
          {options.length === 0 &&
          <p className="text-gray-dark italic mt-1.5">There are no available event types to select. If you want to add
                                                      one,
                                                      follow <UILink href={OldUrls.listOfCategories()}>this
                                                                                                       link</UILink> to
                                                      create a category with event types
                                                      and come back to add a form configuration.</p>
          }
          {v1categories.length > 0 && excludeV1 &&
          <Tip className="text-sm mt-2">
            You cannot configure evaluation forms for {v1categories.join('and')}. <a href="#" onClick={() => setShowHelp(true)}
              className="text-gray font-bold border-dashed border-b">Why?</a>
          </Tip>
          }
        </div>
      </ConfigurationFormField>
      {editingRights === EditingRights.Full &&
      <ConfigurationFormField id="defaultLanguage" label="Default language"
        tooltip={<div className="w-72">Workshop Butler uses the default language when it cannot find a field or
                                       section translation
                                       for the requested language. <br/>For example, your event is in
                                       French but you do not have a French translation. In this case, Workshop
                                       Butler uses the default language
                                       to retrieve a translated string.</div>}>
        <LanguageSelector setSelectedLanguage={updateDefaultLanguage} selectedLanguage={rules.defaultLanguage}
          className="w-full"/>
      </ConfigurationFormField>
      }
    </>
  );
}

interface ConfigurationRulesBlockQueryData {
  categories: Category[]
}

const query = gql`
  query ConfigurationRulesBlockQuery
  {
    categories: categories
    {
      id
      name
      version
      types
      {
        id
        name
      }
    }
  }
`;
